import { animate, state, style, transition, trigger } from '@angular/animations';
import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgxSpinnerService } from 'ngx-spinner';
import { isFalsy } from 'utility-types';
import { Genericos } from '@models/base/genericos.model';
import { EstadosComprobantes, RangoFechasValores, TiposComprobantes, TiposMovimiento } from '@models/base/identificadores.model';
import { ComprobanteDetailDto } from '@models/comprobantes/comprobanteDetailDto.model';
import { ComprobanteFilterDto } from '@models/comprobantes/comprobanteFilterDto.model';
import { LiquidacionDeTransferencia, TransferenciaPropietario } from '@models/comprobantes/transferenciaPropietario.model';
import { ComprobantesService } from '@services/comprobantes/comprobantes.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { DialogComprobanteComponent } from '../dialog-comprobante/dialog-comprobante.component';
import { DialogExportacionBancoComponent } from '../dialog-exportacion-banco/dialog-exportacion-banco.component';
import { AccionesDialog } from '@models/base/identificadores.model';

import { SelectionModel } from '@angular/cdk/collections';
import { ExportarArchivoBancoRequest } from '@models/comprobantes/exportarArchivoBanco.model';
import { Router } from '@angular/router';
import { TemplateBancoDto } from '@models/templates-banco/templateBanco.model';
import { TemplatesBancoService } from '@services/templates-banco/templates-banco.service';
/**
 * Liquidaciones agrupadas por transferencia al propietario.
 *
 * El listado normal muestra una fila por comprobante, y como se emite un
 * comprobante por contrato, un propietario con varias propiedades aparece
 * repetido. Pero a ese propietario se le hace UNA sola transferencia por el
 * total. Esta vista refleja eso.
 *
 * Criterio de agrupación: el Id_LoteLiquidacion que estampa la corrida en todos
 * los comprobantes que emite juntos. Los comprobantes anteriores a esa función
 * no lo tienen, y para esos se agrupa por propietario + fecha de emisión.
 */
@Component({
  selector: 'app-listado-transferencias-propietario',
  templateUrl: './listado-transferencias-propietario.component.html',
  styleUrls: ['./listado-transferencias-propietario.component.css'],
  providers: [DatePipe],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class ListadoTransferenciasPropietarioComponent implements OnInit {

  // TotalCalculo y TotalComisiones ahora se guardan en Comprobantes al emitir.
  // En las liquidaciones anteriores vienen en null y la vista muestra un guion.
  columnas: string[] = ['seleccion', 'propietario', 'fechaEmision', 'liquidaciones', 'totalCalculo', 'totalComisiones', 'totalATransferir'];
  columnasConExpansion: string[] = [...this.columnas, 'expandir'];
  columnasDetalle: string[] = ['numeroComprobante', 'propiedad', 'contrato', 'inquilino', 'estado', 'total', 'accion'];

  dataSource = new MatTableDataSource<TransferenciaPropietario>();
  filaExpandida: TransferenciaPropietario | null = null;

  /**
   * Bancos con formato de archivo parametrizado. Se listan los templates y no
   * todos los bancos: exportar a un banco sin template genera un archivo vacío.
   */
  templatesBanco: TemplateBancoDto[] = [];
  idBancoExportacion: number | null = null;

  /** Transferencias tildadas para exportar al banco */
  seleccion = new SelectionModel<TransferenciaPropietario>(true, []);
  exportando = false;

  form!: FormGroup;
  formBuilder: FormBuilder = new FormBuilder;
  lstRangoFecha = Genericos.lstRangoFecha;
  hayDatos = false;

  private paginator!: MatPaginator;
  private sort!: MatSort;

  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.asignarAtributos();
  }

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.asignarAtributos();
  }

  private asignarAtributos() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  constructor(
    private comprobantesService: ComprobantesService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private datePipe: DatePipe,
    private templatesBancoService: TemplatesBancoService,
    private router: Router,
    public dialog: MatDialog
  ) {
    this.form = this.formBuilder.group({
      FechaDesde: [null],
      FechaHasta: [null],
      RangoFecha: [RangoFechasValores.Trimestral],
      IncluirAnulados: [false]
    });

    // Las fechas salen del mismo rango que muestra el combo. Antes se calculaban
    // aparte (dos meses) mientras el combo decía "Trimestral": el usuario veía un
    // rango que no era el que decía la pantalla.
    this.aplicarRangoFecha(RangoFechasValores.Trimestral);
  }

  /**
   * Calcula Desde/Hasta a partir del rango rápido.
   *
   * Los valores del enum son milisegundos, así que "hasta" es siempre hoy y
   * "desde" se corre hacia atrás. Se normalizan las horas: desde al arranque del
   * día y hasta al final, para que no se pierdan comprobantes de hoy por la hora.
   */
  private aplicarRangoFecha(rango: number) {
    const ahora = new Date().getTime();

    // "Todos" también se estira hacia adelante: hay comprobantes con fecha futura.
    const hasta = rango == RangoFechasValores.Todos ? new Date(ahora + rango) : new Date();
    hasta.setHours(23, 59, 59, 999);

    const desde = new Date(ahora - rango);
    desde.setHours(0, 0, 0, 0);

    this.form.controls["FechaDesde"].setValue(desde);
    this.form.controls["FechaHasta"].setValue(hasta);
  }

  ngOnInit(): void {
    this.buscar();
    this.getTemplatesBanco();
  }

  /**
   * Bancos disponibles para exportar. Se listan los templates y no todos los
   * bancos: exportar a un banco sin formato parametrizado da un archivo vacío.
   * Si hay uno solo queda preseleccionado, que es el caso normal.
   */
  getTemplatesBanco() {
    this.templatesBancoService.getAllConDetalles().subscribe(
      data => {
        this.templatesBanco = isFalsy(data) ? [] : data.filter(t => !isFalsy(t.banco));

        if (this.templatesBanco.length === 1)
          this.idBancoExportacion = this.templatesBanco[0].banco.id;
      },
      error => this.snackBar.showError(error, "Error")
    );
  }

  /** Parametrización del formato del archivo, por si hay que corregir un campo */
  irAParametrizacion() {
    this.router.navigate(['/templatesbanco']);
  }

  //#region Búsqueda

  buscar() {
    const desde = this.form.controls["FechaDesde"].value;
    const hasta = this.form.controls["FechaHasta"].value;

    if (isFalsy(desde) || isFalsy(hasta)) {
      this.snackBar.showError("Elegí un rango de fechas completo.", "Atencion");
      return;
    }

    // Un rango al revés devuelve vacío sin explicar nada: se avisa.
    if (new Date(desde) > new Date(hasta)) {
      this.snackBar.showError("La fecha 'Desde' es posterior a la fecha 'Hasta'.", "Atencion");
      return;
    }

    this.spinner.show("spTransferencias");

    // ComprobanteFilterDto es una interfaz: se arma como objeto literal
    const filtro: ComprobanteFilterDto = {
      idTipoComprobante: TiposComprobantes.Liquidacion,
      fechaDesde: this.formatearFecha(this.form.controls["FechaDesde"].value),
      fechaHasta: this.formatearFecha(this.form.controls["FechaHasta"].value)
    };

    this.comprobantesService.getAllByFilter(filtro).subscribe(
      data => {
        this.spinner.hide("spTransferencias");
        this.agrupar(data);
      },
      error => {
        this.spinner.hide("spTransferencias");
        this.snackBar.showError(error, "Error");
      }
    );
  }

  private formatearFecha(valor: any): string | null {
    if (isFalsy(valor))
      return null;

    return this.datePipe.transform(valor, 'yyyy-MM-dd');
  }

  /** Mismo criterio que el listado de liquidaciones, para que se comporten igual */
  selectRangoFecha(event: any) {
    this.aplicarRangoFecha(event.value);
    this.buscar();
  }

  limpiar() {
    // También se repone el combo: si sólo se tocaban las fechas quedaba diciendo
    // un rango distinto al que estaba aplicado.
    this.form.controls["RangoFecha"].setValue(RangoFechasValores.Trimestral);
    this.form.controls["IncluirAnulados"].setValue(false);

    this.aplicarRangoFecha(RangoFechasValores.Trimestral);
    this.buscar();
  }

  //#endregion

  //#region Agrupación

  /**
   * Arma una fila por propietario y fecha de emisión.
   *
   * Los comprobantes anulados se listan en el detalle pero NO suman al total:
   * por una liquidación anulada no se transfiere plata.
   */
  private agrupar(comprobantes: ComprobanteDetailDto[]) {
    const grupos = new Map<string, TransferenciaPropietario>();

    comprobantes
      .filter(c => c.cabeceraComprobante.tipoComprobante.id == TiposComprobantes.Liquidacion)
      .forEach(comprobante => {
        const cabecera = comprobante.cabeceraComprobante;

        const idPropietario = isFalsy(cabecera.propietario) ? 0 : cabecera.propietario.id;
        const fecha = isFalsy(cabecera.fechaEmision) ? "" : this.datePipe.transform(cabecera.fechaEmision, 'yyyy-MM-dd') ?? "";

        // El lote identifica la corrida y es el criterio correcto. Los comprobantes
        // emitidos antes de que existiera vienen en null: para esos se mantiene el
        // criterio viejo (propietario + fecha), que es lo mejor que se puede inferir.
        const clave = isFalsy(cabecera.idLoteLiquidacion)
          ? `${idPropietario}|${fecha}`
          : `lote:${cabecera.idLoteLiquidacion}`;

        let grupo = grupos.get(clave);

        if (!grupo) {
          grupo = new TransferenciaPropietario();
          grupo.clave = clave;
          grupo.idPropietario = idPropietario;
          grupo.propietario = isFalsy(cabecera.propietario) || isFalsy(cabecera.propietario.persona)
            ? "Sin propietario"
            : cabecera.propietario.persona.descripcion;
          grupo.fechaEmision = isFalsy(cabecera.fechaEmision) ? "" : cabecera.fechaEmision;
          grupos.set(clave, grupo);
        }

        const liquidacion = this.armarLiquidacion(comprobante);
        grupo.liquidaciones.push(liquidacion);
        grupo.cantidadLiquidaciones++;

        if (liquidacion.anulado) {
          grupo.cantidadAnuladas++;
          return;   // no suma: no se transfiere por una liquidación anulada
        }

        grupo.totalDebe += liquidacion.totalDebe;
        grupo.totalHaber += liquidacion.totalHaber;
        grupo.totalPago += liquidacion.totalPago;
        grupo.totalATransferir += liquidacion.total;

        // Totales guardados al emitir. Sólo existen de esta versión en adelante:
        // si falta alguno, el grupo entero queda sin desglose en vez de mostrar
        // una suma parcial que no cerraría contra el comprobante.
        if (isFalsy(cabecera.totalCalculo) || isFalsy(cabecera.totalComisiones))
          grupo.tieneDesglose = false;
        else {
          grupo.totalCalculo += cabecera.totalCalculo as number;
          grupo.totalComisiones += cabecera.totalComisiones as number;
        }
      });

    const filas = Array.from(grupos.values())
      .map(g => {
        g.totalDebe = this.redondear(g.totalDebe);
        g.totalHaber = this.redondear(g.totalHaber);
        g.totalPago = this.redondear(g.totalPago);
        g.totalATransferir = this.redondear(g.totalATransferir);
        g.totalCalculo = this.redondear(g.totalCalculo);
        g.totalComisiones = this.redondear(g.totalComisiones);
        return g;
      })
      // Más recientes primero, y dentro de la misma fecha por propietario
      .sort((a, b) => {
        const comparacionFecha = (b.fechaEmision || "").localeCompare(a.fechaEmision || "");
        return comparacionFecha !== 0 ? comparacionFecha : a.propietario.localeCompare(b.propietario);
      });

    this.seleccion.clear();
    this.dataSource.data = filas;
    this.hayDatos = filas.length > 0;
  }

  /**
   * Calcula los totales de un comprobante con el mismo criterio que el listado
   * de liquidaciones, para que los números coincidan entre las dos vistas.
   */
  private armarLiquidacion(comprobante: ComprobanteDetailDto): LiquidacionDeTransferencia {
    const cabecera = comprobante.cabeceraComprobante;
    const liquidacion = new LiquidacionDeTransferencia();

    liquidacion.idComprobante = cabecera.idComprobante;
    liquidacion.numeroComprobante = isFalsy(cabecera.numeroComprobante) ? "" : cabecera.numeroComprobante;
    liquidacion.estado = isFalsy(cabecera.estadoComprobante) ? "" : cabecera.estadoComprobante.descripcion;
    liquidacion.anulado = !isFalsy(cabecera.estadoComprobante) && cabecera.estadoComprobante.id == EstadosComprobantes.Anulado;
    liquidacion.propiedad = isFalsy(cabecera.propiedad) ? "" : cabecera.propiedad.ubicacion;
    liquidacion.numeroContrato = isFalsy(cabecera.contrato) ? 0 : cabecera.contrato.numeroContrato;
    liquidacion.inquilino = isFalsy(cabecera.inquilino) || isFalsy(cabecera.inquilino.persona)
      ? "" : cabecera.inquilino.persona.descripcion;
    liquidacion.comprobanteDetailDto = comprobante;

    comprobante.detalleComprobante.forEach(detalle => {
      // Egreso e Ingreso de caja quedan fuera: son contrapartidas internas, no
      // parte de lo que se le rinde al propietario.
      if (!isFalsy(detalle.movimiento) &&
        detalle.movimiento.tipoMovimiento.id != TiposMovimiento.EgresoCaja &&
        detalle.movimiento.tipoMovimiento.id != TiposMovimiento.IngresoCaja) {

        if (detalle.movimiento.tipoMovimiento.resta == true)
          liquidacion.totalDebe += isFalsy(detalle.movimiento.importe) ? 0 : detalle.movimiento.importe;
        else
          liquidacion.totalHaber += isFalsy(detalle.movimiento.importe) ? 0 : detalle.movimiento.importe;
      }

      if (!isFalsy(detalle.detalleServicioPropiedad))
        liquidacion.totalDebe += isFalsy(detalle.detalleServicioPropiedad.importeATenerEnCuenta)
          ? 0 : detalle.detalleServicioPropiedad.importeATenerEnCuenta;

      if (!isFalsy(detalle.periodoContrato))
        liquidacion.totalHaber += isFalsy(detalle.periodoContrato.importeAlquiler) ? 0 : detalle.periodoContrato.importeAlquiler;
    });

    if (!isFalsy(cabecera.pagos))
      cabecera.pagos.forEach(pago => liquidacion.totalPago += pago.importe);

    liquidacion.totalDebe = this.redondear(liquidacion.totalDebe);
    liquidacion.totalHaber = this.redondear(liquidacion.totalHaber);
    liquidacion.totalPago = this.redondear(liquidacion.totalPago);
    liquidacion.total = this.redondear(liquidacion.totalPago + liquidacion.totalHaber - liquidacion.totalDebe);

    return liquidacion;
  }

  private redondear(valor: number): number {
    return Math.round((valor || 0) * 100) / 100;
  }

  //#endregion

  //#region Totales del pie

  getTotalGeneral(): number {
    return this.redondear(this.dataSource.data.reduce((suma, g) => suma + g.totalATransferir, 0));
  }

  getCantidadTransferencias(): number {
    return this.dataSource.data.length;
  }

  //#endregion


  //#region Selección y exportación al banco

  estanTodasSeleccionadas(): boolean {
    return this.dataSource.data.length > 0 && this.seleccion.selected.length === this.dataSource.data.length;
  }

  alternarTodas() {
    if (this.estanTodasSeleccionadas())
      this.seleccion.clear();
    else
      this.seleccion.select(...this.dataSource.data);
  }

  getTotalSeleccionado(): number {
    return this.redondear(this.seleccion.selected.reduce((suma, g) => suma + g.totalATransferir, 0));
  }

  /**
   * Genera el archivo para importar en el banco con las transferencias tildadas.
   *
   * Se mandan los ids de los comprobantes, no los totales ya calculados: el
   * backend arma el archivo desde los datos reales y consolida por propietario.
   * Enviar importes desde el navegador sería confiar en el cliente para algo
   * que mueve plata.
   *
   * Los comprobantes anulados no se incluyen, ni acá ni en el backend.
   */
  exportarArchivoBanco() {
    if (this.seleccion.isEmpty()) {
      this.snackBar.showError("Seleccioná al menos una transferencia para exportar.", "Atencion");
      return;
    }

    const idsComprobantes: number[] = [];

    this.seleccion.selected.forEach(grupo => {
      grupo.liquidaciones
        .filter(l => !l.anulado)
        .forEach(l => idsComprobantes.push(l.idComprobante));
    });

    if (idsComprobantes.length === 0) {
      this.snackBar.showError("Las transferencias seleccionadas sólo tienen liquidaciones anuladas.", "Atencion");
      return;
    }

    if (isFalsy(this.idBancoExportacion)) {
      this.snackBar.showError("Elegí el banco al que vas a importar el archivo.", "Atencion");
      return;
    }

    const request = new ExportarArchivoBancoRequest();
    request.idsComprobantes = idsComprobantes;
    request.idBanco = this.idBancoExportacion;

    // El archivo mueve plata y una vez importado en el homebanking no se puede
    // deshacer: se confirma con el detalle a la vista antes de generarlo.
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = "70%";
    dialogConfig.autoFocus = false;
    dialogConfig.data = request;

    const dialogRef = this.dialog.open(DialogExportacionBancoComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (!isFalsy(result) && result.event === AccionesDialog.Aceptar)
        this.generarArchivoBanco(request);
    });
  }

  /** Genera y descarga el archivo. Se llama sólo después de confirmar el modal. */
  private generarArchivoBanco(request: ExportarArchivoBancoRequest) {
    this.exportando = true;
    this.spinner.show("spTransferencias");

    this.comprobantesService.exportarArchivoBanco(request).subscribe(
      data => {
        this.exportando = false;
        this.spinner.hide("spTransferencias");

        if (isFalsy(data) || isFalsy(data.fileContent)) {
          this.snackBar.showError("No se generó el archivo.", "Error");
          return;
        }

        this.descargarArchivo(data.fileContent, data.fileName);
        this.snackBar.showSuccess(data.message, "Exito");
      },
      error => {
        this.exportando = false;
        this.spinner.hide("spTransferencias");
        this.snackBar.showError(error, "Error");
      }
    );
  }

  /** El archivo del banco es texto plano de ancho fijo, no un PDF */
  private descargarArchivo(contenidoBase64: string, nombreArchivo: string) {
    const link = document.createElement("a");
    link.href = "data:text/plain;base64," + contenidoBase64;
    link.download = isFalsy(nombreArchivo) ? "transferencias.txt" : nombreArchivo;
    link.click();
  }

  //#endregion

  verComprobante(liquidacion: LiquidacionDeTransferencia) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.width = "70%";
    dialogConfig.data = liquidacion.comprobanteDetailDto;

    this.dialog.open(DialogComprobanteComponent, dialogConfig);
  }

  expandir(grupo: TransferenciaPropietario) {
    this.filaExpandida = this.filaExpandida === grupo ? null : grupo;
  }

  trackByItems(index: number, item: any): any { return item; }
}
